import { t } from "@/i18n-js/instance";
import type { CommunityMember } from "@/react/types/CommunityMember";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import { usePunditUserContext } from "@circle-react/contexts";

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === "string");

const getInitials = (firstName?: string, lastName?: string): string => {
  if (!firstName && !lastName) return "";

  const initials = [];
  if (firstName) initials.push([...firstName][0]);
  if (lastName) initials.push([...lastName][0]);

  return initials.join("").toUpperCase();
};

interface CommunityPreviewData {
  displayName: string;
  customerJourneyStage?: string;
  achieveWithCircle?: string;
  buildingBlocks: string[];
  userInitials: string;
  fullName: string;
  email?: string;
}

export const useCommunityPreviewData = (
  communityName: string,
  customBuildingBlocks?: string[],
): CommunityPreviewData => {
  const { currentUser } = useCurrentCommunityContext() as {
    currentUser: CommunityMember;
  };
  const { currentCommunity } = usePunditUserContext();
  const localeScope =
    "authentication_app.new_onboarding_flow_april_2025.community_preview";

  const buildingBlocksValue = t(`${localeScope}.building_blocks`, {
    returnObjects: true,
  });
  const defaultBuildingBlocks = isStringArray(buildingBlocksValue)
    ? buildingBlocksValue
    : [];

  const blocks = customBuildingBlocks
    ? [...defaultBuildingBlocks, ...customBuildingBlocks]
    : defaultBuildingBlocks;

  const firstName = currentUser?.first_name ?? "";
  const lastName = currentUser?.last_name ?? "";
  const userInitials = getInitials(firstName, lastName);
  const fullName = [firstName, lastName].filter(Boolean).join(" ");

  const customerJourneyStage =
    currentCommunity?.sign_up_flow_responses?.customer_journey_stage;
  const achieveWithCircle =
    currentCommunity?.sign_up_flow_responses?.achieve_with_circle;

  const displayName = communityName || currentCommunity?.name || "";

  return {
    displayName,
    customerJourneyStage,
    achieveWithCircle,
    buildingBlocks: blocks,
    userInitials,
    fullName,
    email: currentUser?.email,
  };
};
